<template>
  <internal-process
    class="pending-funds-undo-changes-container"
    @go-back="backHandler"
    :process-name="processTitle"
    :with-main-background="withMainBackground"
    :with-footer-background="withFooterBackground"
  >
    <div slot="intr-process-action-bar">
      <mds-button-group
        :content="buttonGroupContent"
        class="pending-undo-changes-switch"
        @mds-button-group-item-active="setActiveItem"
      />
    </div>
    <div class="fc-intr-process__vertical-scroll-box">
      <div class="undo-changes-wrapper" v-if="rows.length > 0">
      <p class="pending-title">
        {{ getSectionTitle }} {{ currentClientName }}
      </p>
      <div>
        <mds-search-field
          id="filter-funds-search"
          class="filter-funds-search"
          :placeholder="getSearchPlaceholder"
          label="Search"
          v-model="searchFilter"
        ></mds-search-field>
      </div>
      </div>
      <div class="grid-wrapper"
        id="pending-status-grid-wrapper"
        :class="{
            'no-scrolled': !horizontallyScrolled,
            'scrolled': horizontallyScrolled,
            'scroll-ends': horizontallyScrollEnds || isListEmpty,
          }"
      >
        <div class="pending-status grid" :class="{ 'empty-grid': (loading || rows.length === 0) }">
          <template v-if="loading">
            <mds-loader />
          </template>
          <template v-else>
            <template v-if="rows.length > 0">
              <DataTable
                :columnsConfig="columnsConfig"
                :data="paginatedData"
                class="table-container fixed-header-table"
              >
                <template v-slot:header-cell="{ content, col }">
                  <div v-if="columnsConfig[col].key === 'select'" class="pending-universe__check">
                    <template>
                      <PendingCheckBox
                        class="pending-status__select-all--checkbox"
                        :indeterminate="isIndeterminateSelected"
                        :checked="isTotalSelected"
                        @change="selectAllClients"
                      />
                    </template>
                  </div>
                  <template v-else>
                    {{ content }}
                  </template>
                </template>
                <template v-slot:cell="{ content, row, col, sub }">
                  <div v-if="columnsConfig[col].key === 'select'" class="pending-universe__check">
                    <template>
                      <PendingCheckBox
                        class="pending-status__select-fund--checkbox"
                        v-if="sub === undefined"
                        :indeterminate="paginatedData[row].isIndeterminateSelected"
                        :checked="content"
                        @change="selectFund(...arguments, row)"
                      />
                    </template>
                  </div>
                  <!-- SUBS -->
                  <div
                    v-else-if="columnsConfig[col].key === 'name'"
                    :class="{
                      fund: sub === undefined,
                      client: sub !== undefined,
                    }"
                  >
                    <template>
                      <template>
                        <PendingCheckBox
                          v-if="sub !== undefined && paginatedData[row].subs[sub] !== undefined "
                          :checked="paginatedData[row].subs[sub].select"
                          @change="selectClient(...arguments, row, sub)"
                          class="pending-universe__check pending-status__select-client--checkbox"
                        />
                      </template>
                    </template>
                    <template>
                      <template v-if="sub === undefined">
                        <CollapseButton
                          class="pending-status__collapse-button"
                          :collapsed="paginatedData[row].__hidden"
                          @toggle="toggleClients(row)"
                        >
                          <span>{{ content }}</span>
                        </CollapseButton>
                      </template>
                      <template v-else>
                        {{ content }}
                      </template>
                    </template>
                  </div>
                  <div v-else-if="columnsConfig[col].key === 'previousStatus'">
                    <StatusIcons v-if="content" :status="[{ name: content }]" :fullLength="true" />
                  </div>
                  <div v-else-if="columnsConfig[col].key === 'updatedStatus'">
                    <StatusIcons v-if="content" :status="[{ name: content }]" :fullLength="true" />
                  </div>
                  <template v-else>{{ content || "—" }}</template>
                </template>
              </DataTable>

              <portal to="pending-status-paginator-portal">
                <div class="pending-status__pager">
                  <mds-pagination
                    v-if="!isEmptyList"
                    :key="activeTab"
                    show-items-info
                    show-items-select
                    :page="currentPage"
                    :pageSizes="pageSizes"
                    :pageSize="pageSize"
                    :total-items="totalRows"
                    @mds-pagination-page-changed="paginationConfigChanged"
                  ></mds-pagination>
                </div>
              </portal>

            </template>
            <template v-else>
              <p class="title-message">Changes to Fund Statuses have not been added</p>
            </template>
          </template>
        </div>
      </div>
      <portal-target name="pending-status-paginator-portal">
        <!-- portal -->
      </portal-target>
    </div>
    <div class="bottom-bar" v-if="rows.length > 0">
      <mds-button
        id="undo-changes-production"
        variation="primary"
        :disabled="!(itemsSelected !== 0)"
        @click="openSubmitToProductionModal"
      >
        Undo Changes
      </mds-button>
    </div>
    <modal />
  </internal-process>
</template>

<script>
import { format } from 'date-fns';
import { mapState, mapActions } from 'vuex';
import {
  groupBy,
  get,
  mapValues,
  cloneDeep,
  orderBy,
  map,
  maxBy,
  minBy,
  sortBy,
  isEmpty,
  chunk,
} from 'lodash';

import MdsPagination from '@mds/pagination';
import MdsSearchField from '@mds/search-field';
import PendingCheckBox from '@/components/atoms/PendingCheckBox';
import StatusIcons from '@/components/atoms/StatusIcons';
import CollapseButton from '@/components/atoms/CollapseButton';
import DataTable from '@/components/molecules/data-table/table';
import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';
import { config } from '@/config/pending-funds-undo-changes.json';
import Modal from '@/components/atoms/Modal';
import MdsButtonGroup from '@mds/button-group';
import SeriesStepperMixin from '@/mixins/SeriesStepperMixin';
import * as PendingFundsService from '@/services/PendingFunds';

const columnsStatusConfig = require('@/config/pending-status-undo-changes-columns.json');
const columnsSeriesConfig = require('@/config/pending-series-undo-changes-columns.json');

const statusChanges = {
  id: 'status-changes',
  label: 'Status Changes',
};
const seriesChanges = {
  id: 'series-changes',
  label: 'Series Changes',
};
const tablePageConfig = {
  page: 1,
  pageSize: 10,
  pageSizes: [10, 25, 50, 100],
};

export default {
  name: 'pending-funds-undo-changes',
  components: {
    Modal,
    MdsButtonGroup,
    MdsPagination,
    MdsSearchField,
    PendingCheckBox,
    StatusIcons,
    CollapseButton,
    DataTable,
  },
  mixins: [
    SeriesStepperMixin(config),
    ErrorNotificationMixin({ component: 'PendingFundsUndoChanges' }),
  ],
  props: {
    selectedClientId: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      scrolled: false,
      horizontallyScrolled: false,
      horizontallyScrollEnds: false,
      gridRef: null,
      rows: [],
      data: [],
      loading: false,
      activeTab: statusChanges.id,
      buttonGroupContent: [
        { id: statusChanges.id, text: statusChanges.label, active: true },
        { id: seriesChanges.id, text: seriesChanges.label, active: false },
      ],
      combineData: {},
      listData: [],
      filteredList: [],
      searchFilter: '',
      currentPage: tablePageConfig.page,
      tabPagesInfo: {
        [statusChanges.id]: { page: tablePageConfig.page, pageSize: tablePageConfig.pageSize },
        [seriesChanges.id]: { page: tablePageConfig.page, pageSize: tablePageConfig.pageSize },
      },
      pageSize: tablePageConfig.pageSize,
      pageSizes: tablePageConfig.pageSizes,
      globalDataObject: {
        [statusChanges.id]: [],
        [seriesChanges.id]: [],
      },
    };
  },
  beforeDestroy() {
    this.gridRef && this.gridRef.removeEventListener('scroll', this.handleScroll);
    this.clearSavedList();
  },
  created() {
    this.clearSavedList();
  },
  mounted() {
    this.gridRef = document.getElementById('pending-status-grid-wrapper');
    this.gridRef && this.gridRef.addEventListener('scroll', this.handleScroll);
    // Load Initial Data
    this.loadData();
  },
  computed: {
    ...mapState({
      policies: (state) => state.user.policies,
      show: (state) => state.modal.show,
      apiCallSuccess: (state) => state.modal.apiCallSuccess,
      selectedFiduciary: (state) => state.user.fiduciary.id,
      selectedClientName: (state) => state.pending.client.name,
      searchTerm: (state) => state.pending.searchTerm,
      itemsDisplayed: (state) => state.pending.itemsDisplayed,
      itemsSelectedAll: (state) => state.pending.itemsSelected,
      statusChangesList: (state) => state.pending.itemsSelected.statusChangesList,
      seriesChangesList: (state) => state.pending.itemsSelected.seriesChangesList,
      statusChanges: (state) => state.pending.itemsSelected.statusChanges,
      seriesChanges: (state) => state.pending.itemsSelected.seriesChanges,
      categoryChanges: (state) => state.pending.itemsSelected.categoryChanges,
      totalItemsSelected: (state) => state.pending.itemsSelected.total,
      selectedTab: (state) => state.pending.tab,
      needsReloading: (state) => state.pending.needsReloading,
      clientList: (state) => state.grid.clientList,
    }),
    columnsConfig() {
      return this.activeTab === statusChanges.id ? columnsStatusConfig : columnsSeriesConfig;
    },
    itemsSelected() {
      return this.activeTab === statusChanges.id ? this.statusChangesList.length : this.seriesChangesList.length;
    },
    itemsSelectedList() {
      return this.activeTab === statusChanges.id ? this.statusChangesList : this.seriesChangesList;
    },
    getSectionTitle() {
      const statusTitle = 'Select the status changes that you want to undo.';
      const seriesTitle = 'Select the series changes that you want to undo.';
      return this.activeTab === statusChanges.id ? statusTitle : seriesTitle;
    },
    getSearchPlaceholder() {
      const searchTitle = 'Search by fund name, SecID';
      const seriesTitle = `${searchTitle}, Series ID`;
      return this.activeTab === statusChanges.id ? searchTitle : seriesTitle;
    },
    isListEmpty() {
      return this.itemsDisplayed === 0;
    },
    currentClientName() {
      if (Number.isNaN(this.selectedClientId) || this.selectedClientId === 'all' || this.selectedClientId === 0) {
        return '( All Clients )';
      }
      const clientSelected = this.clientList.filter((item) => +item.id === +this.selectedClientId)[0];
      return `( ${clientSelected?.client?.clientName} )`;
    },
    currentViewName() {
      return this.selectedClientId > 0 ? 'one-client' : 'all-clients';
    },
    localPolicyOptions() {
      return get(this.policies, `pending.views.${this.currentViewName}.tabs.${this.selectedTab}`);
    },
    activeColumn() {
      const [activeColumn] = this.columnsConfigData.filter(
        (col) => col.sortDirection !== undefined && col.sortDirection !== 0,
      );
      return activeColumn;
    },
    selectedRows() {
      return this.rows.reduce((items, item) => {
        const subs = item.subs.filter((sub) => sub.select === true);
        if (subs.length > 0) {
          items.push(...subs);
        }
        return items;
      }, []);
    },
    totalSubs() {
      return this.rows.reduce((ac, item) => ac + item.subs.length, 0);
    },
    isIndeterminateSelected() {
      const { selectedRows, totalSubs } = this;
      return selectedRows.length > 0 && selectedRows.length < totalSubs;
    },
    isTotalSelected() {
      const { selectedRows, totalSubs } = this;
      return selectedRows.length > 0 && selectedRows.length === totalSubs;
    },
    totalRows() {
      return (Array.isArray(this.filteredList) && this.filteredList.length) || 0;
    },
    totalPages() {
      const realValue = (this.totalRows / this.pageSize);
      const roundedValue = Math.ceil(realValue);
      return roundedValue;
    },
    paginatedData() {
      const filterParam = this.pageSize === -1 ? this.filteredList.length : this.pageSize;
      const data = chunk(this.filteredList, filterParam);
      return data[this.currentPage - 1] || [];
    },
    isEmptyList() {
      return !this.listData || this.listData.length === 0;
    },
  },
  watch: {
    searchFilter() {
      this.filterDataOnWatch();
    },
    show() {
      // if modal is closed with api success, reload the data
      if (!this.show && this.apiCallSuccess) {
        this.loadData();
      }
    },
  },
  methods: {
    ...mapActions([
      'setPendingItemsDisplayed',
      'setPendingItemsSelected',
      'logError',
    ]),
    filterCallback(listData, searchTerm) {
      const arrayFilterBy = ['fundName', 'secid', 'previousSeries', 'updatedSeries'];

      const findByProperty = (listDataChanges, property) => {
        const regE = new RegExp(searchTerm, 'gi');

        const resultByTagSelected = listDataChanges.reduce((acc, curr) => {
          const seriesQuery = curr.subs[0][`${property}`];
          const statusAndSeriesQuery = curr[`${property}`];

          if (this.activeTab === seriesChanges.id) {
            if (seriesQuery !== undefined && seriesQuery !== null && seriesQuery.match(regE)) {
              return [...acc, curr];
            }
          }
          if (statusAndSeriesQuery !== undefined && statusAndSeriesQuery.match(regE)) {
            return [...acc, curr];
          }
          return acc;
        }, []);
        return resultByTagSelected;
      };

      const resultByProperty = arrayFilterBy.map((property) => findByProperty(listData, property));

      const filteredResults = resultByProperty.reduce((accChanges, currentChange) => {
        if (currentChange.length !== 0 && !accChanges.includes(...currentChange)) {
          return [...accChanges, ...currentChange];
        }
        return accChanges;
      }, []);
      return filteredResults;
    },
    columnsConfigData() {
      return this.activeTab === statusChanges.id ? columnsStatusConfig : columnsSeriesConfig;
    },
    findCheckedRows(rows) {
      return rows.reduce((items, item) => {
        const subs = item.subs.filter((sub) => sub.select === true);
        if (subs.length > 0) {
          items.push(...subs);
        }
        return items;
      }, []);
    },
    clearSavedList() {
      const updateObj = {
        total: 0,
        list: [],
        seriesChangesList: [],
        statusChanges: [],
        statusChangesList: [],
        seriesChanges: [],
      };
      this.$store.dispatch('setPendingItemsSelected', updateObj);
    },
    openSubmitToProductionModal() {
      /** @component components/molecules/SubmitChange */
      let selectedFundsList = this.seriesChangesList;
      if (this.activeTab === statusChanges.id) {
        selectedFundsList = this.statusChangesList;
      }
      this.searchFilter = '';
      this.$store.dispatch('openModal', {
        title: 'Undo Status Change',
        component: 'undo-changes',
        data: { funds: selectedFundsList, shareclassName: '' },
      });
    },
    handleScroll(event) {
      const grid = event.currentTarget;
      if (grid.scrollLeft && grid.scrollLeft > 20) {
        this.horizontallyScrolled = true;
        if ((grid.clientWidth + grid.scrollLeft) >= grid.scrollWidth) {
          this.horizontallyScrollEnds = true;
        }
        return true;
      }
      this.horizontallyScrollEnds = false;
      this.horizontallyScrolled = false;
      return false;
    },
    statusChangesFilter(resultData, previousFundSeriesKey, updatedFundSeriesKey) {
      return resultData.filter((item) => (isEmpty(item[previousFundSeriesKey]) && isEmpty(item[updatedFundSeriesKey])));
    },
    seriesChangesFilter(resultData, previousFundSeriesKey, updatedFundSeriesKey) {
      return resultData.filter((item) => (!isEmpty(item[previousFundSeriesKey]) || !isEmpty(item[updatedFundSeriesKey])));
    },
    getStatusData(resultData) {
      const resultDataUpdated = this.statusChangesFilter(resultData, 'previousFundSeries', 'updatedFundSeries');
      const formatedData = this.formatData(resultDataUpdated);
      return this.filterData(formatedData);
    },
    getSeriesData(resultData) {
      const resultDataUpdated = this.seriesChangesFilter(resultData, 'previousFundSeries', 'updatedFundSeries');
      const formatedData = this.formatData(resultDataUpdated);
      return this.filterData(formatedData);
    },
    loadData() {
      this.$store.dispatch('setPendingItemsDisplayed', 0);
      this.clearSavedList();
      this.loading = true;
      this.data = [];
      this.rows = [];

      PendingFundsService.getPendingFundStatusChangesList({
        selectedFiduciary: this.selectedFiduciary,
      }).then((result) => {
        this.currentPage = 1;
        if (get(result, 'status.success')) {
          if (Array.isArray(result.data) && result.data.length > 0) {
            const resultData = result.data;
            this.combineData[statusChanges.id] = this.getStatusData(resultData);
            this.combineData[seriesChanges.id] = this.getSeriesData(resultData);
            // All the data fetched from the API
            this.data = this.combineData[this.activeTab] || [];
            this.$store.dispatch('setPendingItemsDisplayed', this.data.length);
            // If a filter has been applied we get the filetered data here
            // otherwhise all the data will be stored into the rows
            this.rows = this.combineData[this.activeTab];
            this.sortData('date', -1);
            // These are the data properties needed to have the pagination feature
            this.listData = this.rows;
            this.filteredList = this.listData;
          }
        }
        this.loading = false;
      }).catch((error) => {
        const httpStatus = (error?.response?.status) ? (error?.response?.status) : error?.status;
        this.loading = false;
        if (httpStatus === 401) {
          this.$router.push('/Login');
        }
        return false;
      }).finally(() => {
        this.$store.dispatch('setReloadNeed', false);
      });
    },
    formatData(data) {
      const formattedData = [];
      const groupedData = mapValues(groupBy(data, 'secID'), (v) => sortBy(v, 'client.clientName'));

      try {
        /* eslint-disable-next-line no-restricted-syntax */
        for (const property in groupedData) {
          if (Object.prototype.hasOwnProperty.call(groupedData, property)) {
            const newItem = {
              id: get(groupedData, `[${property}][0].secID`),
              name: get(groupedData, `[${property}][0].fundName`),
              fundName: get(groupedData, `[${property}][0].fundName`),
              ticker: get(groupedData, `[${property}][0].ticker`),
              secid: get(groupedData, `[${property}][0].secID`),
              actions: map(groupedData[property], 'fiduciaryFundID'),
              select: false,
              isIndeterminateSelected: false,
              subs: [],
              __hidden: false,
            };

            /* eslint-disable-next-line no-plusplus */
            for (let i = 0; i < groupedData[property].length; i++) {
              const {
                fiduciaryClient: { client: { clientName: name = '' } = {}, id: clientId = 0 } = {},
              } = groupedData[property][i];
              const hasPendingSeries = get(groupedData, `[${property}][${i}].hasPendingFundSeries`);
              const previousSeriesDefault = hasPendingSeries ? 'None' : null;
              const updatedSeriesDefault = hasPendingSeries ? 'None' : null;
              const newSub = {
                subIndex: i,
                id: `sub-${get(groupedData, `[${property}][${i}].secID`)}-${clientId}`,
                fundId: get(groupedData, `[${property}][${i}].fiduciaryFundID`),
                actions: [get(groupedData, `[${property}][${i}].fiduciaryFundID`)],
                fundName: get(groupedData, `[${property}][0].fundName`),
                name,
                clientId,
                clientName: name,
                hasPendingStatus: get(groupedData, `[${property}][${i}].hasPendingStatus`),
                hasPendingCategory: get(groupedData, `[${property}][${i}].hasPendingCategory`),
                hasPendingFundSeries: hasPendingSeries,
                previousStatus: get(groupedData, `[${property}][${i}].previousStatus.name`),
                updatedStatus: get(groupedData, `[${property}][${i}].updatedStatus.name`),
                previousSeries: get(groupedData, `[${property}][${i}].previousFundSeries.seriesName`, previousSeriesDefault),
                updatedSeries: get(groupedData, `[${property}][${i}].updatedFundSeries.seriesName`, updatedSeriesDefault),
                date: format(get(groupedData, `[${property}][${i}].dateOfChange`), 'MMM D, YYYY'),
                dateOfChange: get(groupedData, `[${property}][${i}].dateOfChange`),
                select: false,
                __hidden: false,
              };
              newItem.subs.push(newSub);
            }
            formattedData.unshift(newItem);
          }
        }
      } catch (error) {
        this.logError({
          errorFrom: 'PendingStatusChangesGrid:methods:formatData',
          details: {
            ...Object.getOwnPropertyDescriptors(error),
            message: error.message,
          },
        });
      }
      return formattedData;
    },
    filterData(formatedData) {
      const rows = cloneDeep(formatedData);
      const filteredByClient = this.filterByClient(rows);
      const filteredByKeyWord = this.filterCallback(filteredByClient, this.searchFilter);
      return filteredByKeyWord;
    },
    filterByClient(rows) {
      let propsClientId = this.selectedClientId;
      if (Number.isNaN(propsClientId) || propsClientId === 'all') {
        propsClientId = 0;
      }
      if (+propsClientId !== 0) {
        return rows
          .map((row) => {
            const modifRow = cloneDeep(row);
            modifRow.subs = row.subs.filter((sub) => +sub.clientId === +propsClientId);
            return modifRow;
          })
          .filter((row) => row.subs.length > 0);
      }
      return rows;
    },
    filterByKeyword(rows) {
      if (this.searchTerm) {
        return rows.filter((row) => {
          const matchName = new RegExp(this.searchTerm, 'gi').test(row.name);
          const matchTicker = new RegExp(this.searchTerm, 'gi').test(row.ticker);
          const matchsecID = new RegExp(this.searchTerm, 'gi').test(row.secid);
          let matchClientName = false;
          if (row.subs.length) {
            const foundRows = row.subs.filter((innerrow) => {
              return new RegExp(this.searchTerm, 'gi').test(innerrow.clientName);
            });
            if (foundRows.length) { matchClientName = true; }
          }
          return matchName || matchTicker || matchsecID || matchClientName;
        });
      }
      return rows;
    },
    showToolTip(event, message) {
      this.$emit('showToolTip', event, message, undefined, 'small');
    },
    hideToolTip() {
      this.$emit('hideToolTip');
    },
    selectAllClients(checkedValue) {
      /* eslint-disable no-param-reassign */
      this.rows = this.rows.map((item) => {
        item.select = checkedValue;
        item.isIndeterminateSelected = false;
        item.subs = item.subs.map((sub) => {
          sub.select = checkedValue;
          return sub;
        });
        return item;
      });
      this.updateSelectedData();
      /* eslint-enable no-param-reassign */
    },
    selectClient(checkedValue, value, row, sub) {
      const selectedParentRow = this.paginatedData[row];
      const selectedRow = selectedParentRow.subs[sub];
      const totalSubs = selectedParentRow.subs.length;

      selectedRow.select = checkedValue;
      const totalSelected = selectedParentRow.subs.reduce(
        (ac, el) => ac + (el.select === true ? 1 : 0),
        0,
      );
      if (totalSelected === 0) {
        selectedParentRow.select = false;
        selectedParentRow.isIndeterminateSelected = false;
      } else if (totalSelected === totalSubs) {
        selectedParentRow.select = true;
        selectedParentRow.isIndeterminateSelected = false;
      } else {
        selectedParentRow.select = false;
        selectedParentRow.isIndeterminateSelected = totalSelected > 0 && totalSelected < totalSubs;
      }

      this.updateSelectedData();
    },
    selectFund(checkedValue, value, row) {
      const selectedRow = this.paginatedData[row];
      selectedRow.select = checkedValue;
      selectedRow.subs = selectedRow.subs.map((item) => {
        const modifItem = cloneDeep(item);
        modifItem.select = checkedValue;
        return modifItem;
      });
      this.updateSelectedData();
    },
    getActualRow(row) {
      if (this.currentPage > 1) {
        return row + ((this.currentPage - 1) * this.pageSize);
      }
      return row;
    },
    updateSelectedData() {
      const selectedRows = [...this.selectedRows];
      const statusData = this.findCheckedRows(this.combineData[statusChanges.id]);
      const seriesData = this.findCheckedRows(this.combineData[seriesChanges.id]);
      this.$store.dispatch('setPendingItemsSelected', {
        total: selectedRows.length,
        list: [],
        statusChangesList: statusData.map((item) => item.fundId),
        seriesChangesList: seriesData.map((item) => item.fundId),
        statusChanges: selectedRows.filter((item) => item.hasPendingStatus).length,
        seriesChanges: selectedRows.filter((item) => item.hasPendingFundSeries).length,
        categoryChanges: selectedRows.filter((item) => item.hasPendingCategory).length,
      });
    },
    undo(row, sub) {
      const actualRow = this.getActualRow(row);
      const selectedRow = sub !== undefined ? this.rows[actualRow].subs[sub] : this.rows[actualRow];
      const { actions, fundName, clientName } = selectedRow;
      // @/components/atoms/UndoChanges
      this.$store.dispatch('openModal', {
        title: 'Undo Status Change',
        component: 'undo-changes',
        data: { funds: actions, shareclassName: `${fundName} ${clientName ? `/ ${clientName}` : ''}` },
      });
    },
    sortBy(colKey) {
      /* eslint-disable no-param-reassign */
      let direction = 1;
      this.columnsConfig = this.columnsConfigData.map((col) => {
        if (col.key === colKey) {
          if (col.sortDirection !== 0) {
            col.sortDirection *= -1;
          } else {
            col.sortDirection = 1;
          }
          direction = col.sortDirection;
        } else if (col.sortDirection !== undefined) {
          col.sortDirection = 0;
        }
        return col;
      });
      this.sortData(colKey, direction);
      /* eslint-enable no-param-reassign */
    },
    sortData(key, direction) {
      const dir = direction > 0 ? 'asc' : 'desc';
      if (key !== 'date') {
        this.rows = orderBy(this.rows, key, dir);
      } else {
        this.rows = orderBy(
          this.rows,
          (elem) => {
            let time;
            if (direction > 0) {
              time = maxBy(elem.subs, (item) => new Date(item.dateOfChange || item.date).getTime());
            } else {
              time = minBy(elem.subs, (item) => new Date(item.dateOfChange || item.date).getTime());
            }
            return new Date(time.dateOfChange || time.date).getTime();
          },
          dir,
        );
      }
      this.refreshPaginatedData();
    },
    toggleClients(row) {
      const actualRow = this.getActualRow(row);
      const isHidden = !this.rows[actualRow].__hidden;
      this.rows[actualRow].__hidden = isHidden;
      this.rows[actualRow].subs = this.rows[actualRow].subs.map((sub) => {
        const modifSub = cloneDeep(sub);
        modifSub.__hidden = isHidden;
        return modifSub;
      });
      this.refreshPaginatedData();
    },
    refreshPaginatedData() {
      this.listData = this.rows;
      this.filteredList = this.filterData(this.listData);
    },
    setActiveItem(event) {
      /* eslint-disable no-param-reassign */
      this.buttonGroupContent.forEach((item) => {
        this.searchFilter = '';
        if (item.id === event.currentTarget.id) {
          item.active = true;
          this.activeTab = item.id;
          this.resetValuesSelectedTab(this.activeTab);
        } else {
          item.active = false;
        }
      });
      /* eslint-disable no-param-reassign */
    },
    resetValuesSelectedTab(activeTab) {
      this.rows = this.combineData[activeTab];
      this.refreshPaginatedData();
      this.filterDataOnWatch();
      this.currentPage = this.tabPagesInfo[activeTab].page;
      this.pageSize = this.tabPagesInfo[activeTab].pageSize;
      this.pageSizes = tablePageConfig.pageSizes;
      const paginationConfig = {
        page: this.currentPage,
        pageSize: this.pageSize,
        firstItem: +(this.pageSize * (this.currentPage - 1)) + 1,
        lastItem: this.currentPage * this.pageSize,
        pageSizes: this.pageSizes,
        totalItems: this.rows.length,
      };
      this.paginationConfigChanged(paginationConfig);
      this.$store.dispatch('setPendingItemsDisplayed', this.rows.length);
    },
    filterDataOnWatch() {
      this.filteredList = this.listData && this.filterCallback(this.listData, this.searchFilter);
    },
    changePage(newPage) {
      if (newPage !== this.currentPage) {
        this.tabPagesInfo[this.activeTab].page = newPage;
        this.currentPage = newPage;
      }
    },
    changePageSize(newPageSize) {
      if (newPageSize !== this.pageSize) {
        this.tabPagesInfo[this.activeTab].pageSize = newPageSize;
        this.pageSize = newPageSize;
      }
    },
    paginationConfigChanged(pagination) {
      this.tabPagesInfo[this.activeTab].page = pagination.page;
      this.tabPagesInfo[this.activeTab].pageSize = pagination.pageSize;
      this.changePage(pagination.page);
      this.changePageSize(pagination.pageSize);
      this.cleanProcess && this.cleanProcess();
    },
  },
};
</script>

<style lang="scss" scoped>
.undo-changes-wrapper{
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  .pending-title {
      @include mds-level-6-heading();
  }
  div{
    display: block;
    width: 30%;
    min-width: 200px;
    .filter-funds-search{
    width: 100%;
    }
  }
}

.pending-undo-changes-switch {
  display: flex;
  margin: $mds-space-3-x auto;
  justify-content: center;
}

.bottom-bar {
  float: right;
}

.pending-status.grid {
  width: 100%;
  margin: 0 auto;
}

.grid-wrapper {
  width: 100%;
  margin: 0 auto;
  margin-top: $mds-space-4-x;
  height: 450px;
  overflow-y: scroll;
}

.mds-data-table__header {
  .mds-data-table__header-cell-inner div.pending-universe__check {
    margin-top: $mds-space-1-x;
  }
}
</style>>
